// Named middleware that redirect logged-out users away from a route. These are
// the only guards that make a route inaccessible once the user logs out, so
// they decide whether logout needs to bounce the user back to the home page.
export const AUTH_GUARD_MIDDLEWARE = ["authenticated", "admin"];

function getMiddlewareNames(middleware) {
  if (!middleware) {
    return [];
  }
  const list = Array.isArray(middleware) ? middleware : [middleware];
  // Only string-named middleware can be one of our guards; inline-function
  // middleware (e.g. the price-offer pages) only ever redirect logged-in users.
  return list.filter((entry) => typeof entry === "string");
}

function getComponentMiddleware(component) {
  if (!component) {
    return [];
  }
  // Resolved route components / layouts are sanitized into `{ options }`, but be
  // defensive in case we're handed a plain options object.
  const options = component.options ?? component;
  return getMiddlewareNames(options.middleware);
}

/**
 * Whether the given route is protected by an auth guard (the `authenticated`
 * or `admin` middleware). The guard can be declared either on the page
 * component or on its layout (e.g. ConductorLayout uses `admin`), so we mirror
 * Nuxt's own middleware collection and inspect both.
 *
 * @param {object} route - the current route (`router.currentRoute`)
 * @param {object} [layout] - the active layout component (`$nuxt.layout`)
 */
export function isAuthGuardedRoute(route, layout) {
  const middlewareNames = [
    ...getComponentMiddleware(layout),
    ...(route?.matched ?? []).flatMap((record) =>
      getComponentMiddleware(record.components?.default)
    ),
  ];

  return middlewareNames.some((name) => AUTH_GUARD_MIDDLEWARE.includes(name));
}

export const redirectToLogin = ({ route, localePath, redirect }) => {
  const uri = encodeURIComponent(route.fullPath);
  const query = { "callback-url": uri };
  if (route.query.email) {
    query.email = route.query.email;
  }
  if (route.query.noPassword) {
    query.noPassword = route.query.noPassword;
  }
  const path = localePath({
    name: "login",
    query,
  });
  return redirect(path);
};
